import { useRef, useState, useCallback, useEffect } from 'react';

export interface WsMessage {
  type: 'status' | 'chunk' | 'complete' | 'error' | 'pong';
  text?: string;
  message?: string;
  response?: string;
  session_id?: string;
  crisis_detected?: boolean;
}

type Payload = Record<string, string | null>;

interface UseStreamingChatReturn {
  connect: (wsUrl: string, idToken: string) => void;
  disconnect: () => void;
  sendMessage: (message: string, sessionId: string | null, impersonateStudentId?: string | null, language?: string) => void;
  isConnected: boolean;
  isStreaming: boolean;
  streamedText: string;
  lastComplete: WsMessage | null;
  lastError: string | null;
  statusMessage: string | null;
  resetStream: () => void;
}

// 10-minute idle timeout on API Gateway WebSockets means we must transmit
// application data more often than every 10 min. 4 min gives plenty of headroom.
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000;
// Backoff caps for reconnect attempts.
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30_000;

export default function useStreamingChat(): UseStreamingChatReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectArgsRef = useRef<{ wsUrl: string; idToken: string } | null>(null);
  const reconnectAttemptsRef = useRef(0);
  // A user message captured while the socket was dead; flushed on next onopen.
  const pendingPayloadRef = useRef<Payload | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedText, setStreamedText] = useState('');
  const [lastComplete, setLastComplete] = useState<WsMessage | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const resetStream = useCallback(() => {
    setStreamedText('');
    setIsStreaming(false);
    setLastComplete(null);
    setLastError(null);
    setStatusMessage(null);
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const msg: WsMessage = JSON.parse(event.data);

      switch (msg.type) {
        case 'status':
          setStatusMessage(msg.message || null);
          break;

        case 'chunk':
          setIsStreaming(true);
          setStatusMessage(null);
          setStreamedText((prev) => prev + (msg.text || ''));
          break;

        case 'complete':
          setIsStreaming(false);
          setStatusMessage(null);
          setLastComplete(msg);
          break;

        case 'error':
          setIsStreaming(false);
          setStatusMessage(null);
          setLastError(msg.message || 'An error occurred');
          break;

        case 'pong':
          // Heartbeat ack — nothing to do.
          break;
      }
    } catch {
      // Ignore non-JSON messages
    }
  }, []);

  const createWebSocket = useCallback((wsUrl: string, idToken: string) => {
    const ws = new WebSocket(`${wsUrl}?token=${idToken}`);

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Flush any user message that was queued while the socket was dead.
      const pending = pendingPayloadRef.current;
      if (pending && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify(pending));
          pendingPayloadRef.current = null;
          setStatusMessage(null);
        } catch {
          // Leave pending in place; next reconnect will retry.
        }
      }
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      setIsConnected(false);
      wsRef.current = null;

      // Indefinite auto-reconnect with capped exponential backoff. We keep
      // trying as long as we still have args (i.e. the consumer hasn't called
      // disconnect()), so a transient network drop or AWS idle close always
      // recovers on its own.
      if (reconnectArgsRef.current && !reconnectTimerRef.current) {
        const delay = Math.min(
          RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttemptsRef.current,
          RECONNECT_MAX_DELAY_MS,
        );
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          const args = reconnectArgsRef.current;
          if (args) {
            wsRef.current = createWebSocket(args.wsUrl, args.idToken);
          }
        }, delay);
      }
    };

    ws.onerror = () => {
      // onclose will fire after onerror, so reconnect logic is handled there
    };

    return ws;
  }, [handleMessage]);

  // Reconnect from external triggers (heartbeat / sendMessage) when the socket
  // is dead. Idempotent: bails out if a reconnect timer is already scheduled.
  const triggerReconnect = useCallback(() => {
    const args = reconnectArgsRef.current;
    if (!args || reconnectTimerRef.current) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
    }
    reconnectAttemptsRef.current = 0;
    wsRef.current = createWebSocket(args.wsUrl, args.idToken);
  }, [createWebSocket]);

  const sendHeartbeat = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ action: 'ping' }));
      } catch {
        // Send failed despite OPEN state — socket is in a phantom-open
        // condition; force a reconnect.
        triggerReconnect();
      }
    } else {
      triggerReconnect();
    }
  }, [triggerReconnect]);

  const connect = useCallback((wsUrl: string, idToken: string) => {
    // Store args for reconnection and reset retry counter (fresh token = fresh attempts)
    reconnectArgsRef.current = { wsUrl, idToken };
    reconnectAttemptsRef.current = 0;

    // Clear any pending reconnect
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    wsRef.current = createWebSocket(wsUrl, idToken);
  }, [createWebSocket]);

  const disconnect = useCallback(() => {
    reconnectArgsRef.current = null;
    pendingPayloadRef.current = null;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message: string, sessionId: string | null, impersonateStudentId?: string | null, language?: string) => {
    const payload: Payload = {
      action: 'chat',
      message,
      session_id: sessionId,
    };
    if (impersonateStudentId) {
      payload.impersonate_student_id = impersonateStudentId;
    }
    if (language && language !== 'en') {
      payload.language = language;
    }

    // Reset state for new message
    setStreamedText('');
    setLastComplete(null);
    setLastError(null);
    setStatusMessage(null);
    setIsStreaming(true);

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(payload));
        return;
      } catch {
        // Fall through to the reconnect path.
      }
    }

    // Socket is dead (or send failed). Queue the payload, surface a
    // user-visible status, and trigger a reconnect — onopen will flush it.
    pendingPayloadRef.current = payload;
    setStatusMessage('Reconnecting…');
    triggerReconnect();
  }, [triggerReconnect]);

  // Heartbeat loop — runs whenever we have reconnect args (i.e. between
  // connect() and disconnect()). Sends a lightweight ping every 4 minutes
  // so AWS API Gateway's 10-minute idle timeout never elapses, and force-
  // reconnects if the socket is unexpectedly closed.
  useEffect(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
    }
    heartbeatTimerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    return () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [sendHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      reconnectArgsRef.current = null;
      pendingPayloadRef.current = null;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return {
    connect,
    disconnect,
    sendMessage,
    isConnected,
    isStreaming,
    streamedText,
    lastComplete,
    lastError,
    statusMessage,
    resetStream,
  };
}
